import { useCallback, useContext, useMemo, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { usePermissionsContext } from '@/app/contexts/permissions';
import { IntlContext } from '@/i18n';
import { prepareUploadMyDriverForm } from '@/modules/driver/actions/async/prepare-upload-driver-form';
import { DriverDocType } from '@/modules/driver/model/driver-doc-type';
import { myDriverUploadSelectors } from '@/modules/driver/store/upload-slice';
import { prepareUploadPartnerForm } from '@/modules/partner/actions/async/preparePartnerUploadForm';
import { reloadPersonData } from '@/modules/partner/actions/async/reload-person-data';
import { PartnerDocType } from '@/modules/partner/model/partner-doc-type';
import { partnerUploadSelectors } from '@/modules/partner/store/uploadSlice';
import { AppDispatch } from '@/root-store';
import { CountryCode } from '@/shared/models/country-code';
import { StepLayout } from '@/shared/view/StepLayout';
import { UploadMyDriver, UploadPartner } from '@/shared/view/Upload';
import { useCollapsedDocsList } from '@/shared/view/Upload/SupplementaryDocs/useCollapsedDocsList';
import { IDocsListItem } from '@src-common/view/components/DocsList';

import { EditDriverInfo } from './components/gb';
import { EditDriverInfoUs } from './components/us';

interface IProps {
  onNavigateBack: (uri: string) => void;
}

const isDriverDoc = Symbol('isDriverDoc');

const title = defineMessage({
  defaultMessage: 'Chauffeur info',
  id: 'AIUtGz',
  description: 'Edit chauffeur info screen: title',
});

interface IDoc extends IDocsListItem {
  [isDriverDoc]: boolean;
}

const mergeDocListItems = (forPartner: IDocsListItem[], forDriver: IDocsListItem[]): IDoc[] => [
  ...forDriver.map(document => ({ ...document, [isDriverDoc]: true })),
  ...forPartner.map(document => ({ ...document, [isDriverDoc]: false })),
];

const ProfileChauffeur = ({ onNavigateBack }: IProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = usePermissionsContext();
  const { region } = useContext(IntlContext);
  const intl = useIntl();

  const [isDriverUploading, setIsDriverUploading] = useState(false);
  const [isLegalUploading, setIsLegalUploading] = useState(false);

  const handleFinishEdit = useCallback(() => {
    onNavigateBack('/profile');
  }, [onNavigateBack]);

  const handleFinishUpload = useCallback(() => {
    dispatch(reloadPersonData());
    setIsDriverUploading(false);
    setIsLegalUploading(false);
  }, [dispatch]);

  const handleSkipUpload = useCallback(() => {
    setIsDriverUploading(false);
    setIsLegalUploading(false);
  }, []);

  const driverDocs = useCollapsedDocsList(
    useSelector(myDriverUploadSelectors.getDocsList),
    'myDriver',
  );
  const partnerDocs = useSelector(partnerUploadSelectors.getDocsList);
  const mergedDocs = useMemo(
    () =>
      user.isFleet
        ? mergeDocListItems(partnerDocs, [])
        : mergeDocListItems(partnerDocs, driverDocs),
    [driverDocs, partnerDocs, user.isFleet],
  );

  const handleUploadDoc = useCallback(
    (_: any, doc: IDoc) => {
      if (doc[isDriverDoc]) {
        dispatch(
          prepareUploadMyDriverForm({
            docType: doc.docType as DriverDocType,
            onSuccess: () => setIsDriverUploading(true),
          }),
        );
      } else {
        dispatch(
          prepareUploadPartnerForm({
            docType: doc.docType as PartnerDocType,
            onSuccess: () => setIsLegalUploading(true),
          }),
        );
      }
    },
    [dispatch],
  );

  if (region !== CountryCode.GreatBritain && region !== CountryCode.UnitedStates) {
    return null;
  }

  return (
    <>
      <StepLayout
        hideProgress={true}
        title={intl.formatMessage(title)}
        onClickBack={handleFinishEdit}
      >
        {region === CountryCode.GreatBritain && (
          <EditDriverInfo
            docs={mergedDocs}
            onUploadDoc={handleUploadDoc}
            onFinishSubmit={handleFinishEdit}
          />
        )}
        {region === CountryCode.UnitedStates && (
          <EditDriverInfoUs
            docs={mergedDocs}
            onUploadDoc={handleUploadDoc}
            onFinishSubmit={handleFinishEdit}
          />
        )}
      </StepLayout>
      {isDriverUploading && (
        <UploadMyDriver onClickSkip={handleSkipUpload} onFinishUpload={handleFinishUpload} />
      )}
      {isLegalUploading && (
        <UploadPartner onClickSkip={handleSkipUpload} onFinishUpload={handleFinishUpload} />
      )}
    </>
  );
};

export { ProfileChauffeur };
